import { useMemo } from 'react';
import { selectAtom, splitAtom, unwrap } from 'jotai/utils';
import { progAtom } from '@teetsh/app/src/containers/Programmations/atoms/progAtoms';
import { Matiere, Programmation } from '@teetsh/app/src/types/programmations';
import { atom, PrimitiveAtom, useAtomValue } from 'jotai';
import { Color } from 'types/colors';
import { TagColorWithIntensity } from '@teetsh/app/src/styles/userConstants';

export const programmationMatiereIdsAtom = selectAtom<
  Programmation | undefined,
  string[]
>(
  unwrap(progAtom),
  (prog, prev) => {
    if (!prog) {
      return prev || [];
    }
    return (prog?.matieres || []).map(p => p.id);
  },
  (a, b) => (a || []).join('') === (b || []).join(''),
);
export const useMatieresIds = () => useAtomValue(programmationMatiereIdsAtom);

export const useMatiere = (p: PrimitiveAtom<Matiere>) => useAtomValue(p);

export const nbOfMatieresAtom = selectAtom<
  Programmation | undefined,
  number | undefined
>(unwrap(progAtom), (prog, prev) => {
  if (!prog) {
    return prev;
  }
  return (prog?.matieres || []).length;
});
export const useNbOfMatieres = () => useAtomValue(nbOfMatieresAtom);

export const matieresAtom = selectAtom<Programmation | undefined, Matiere[]>(
  unwrap(progAtom),
  (p, prev) => {
    if (!p) {
      return prev || [];
    }
    return p.matieres;
  },
);

export const firstMatiereIdAtom = selectAtom<
  Programmation | undefined,
  string | undefined
>(unwrap(progAtom), prog => {
  if (!prog) {
    return undefined;
  }
  return prog?.matieres[0]?.id;
});

const matieresOnlyAtom = selectAtom<
  Programmation | undefined,
  Array<{ name: string; id: string; color: Color | TagColorWithIntensity }>
>(
  unwrap(progAtom),
  prog => {
    if (!prog) {
      return [];
    }
    return (prog?.matieres || []).map(p => ({
      name: p.name,
      id: p.id,
      color: p.color,
    }));
  },
  (a, b) => JSON.stringify(a) === JSON.stringify(b),
);

export const useMatieresOnly = () => useAtomValue(matieresOnlyAtom);

export const useFirstMatiereId = () => useAtomValue(firstMatiereIdAtom);

export const matieresAtomsAtom = splitAtom(matieresAtom);
export const useMatieres = () => useAtomValue(matieresAtom);

export const useMatieresAtoms = () => useAtomValue(matieresAtomsAtom);

export const useDomainesAtom = (matiereAtom: PrimitiveAtom<Matiere>) =>
  useMemo(() => selectAtom(matiereAtom, m => m.domaines), [matiereAtom]);

export const useDomaineAtoms = (matiereAtom: PrimitiveAtom<Matiere>) => {
  const domainesFocusAtom = useDomainesAtom(matiereAtom);
  const domaineAtomAtoms = splitAtom(domainesFocusAtom);
  return useAtomValue(domaineAtomAtoms);
};

export const flattenDomainesAtoms = atom(get => {
  const matieresAtoms = get(matieresAtomsAtom);
  return matieresAtoms.reduce((acc, matiereAtom) => {
    const domainesAtom = selectAtom(matiereAtom, m => m.domaines);
    const domainesAtoms = splitAtom(domainesAtom as any);
    return [...acc, ...get(domainesAtoms)];
  }, []);
});

export const useFlattenDomainesAtoms = () => useAtomValue(flattenDomainesAtoms);
